import { MouseButton } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { RenderUtils } from '../utils/RenderUtils';
import { ThreeEvent, UserDataProp } from '../types';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { Raycaster, Vector2 } from 'three';

export class InputHandler {
	static readonly LONG_PRESS_DELAY = 550;
	static readonly TOUCH_DESELECT_THRESHOLD = 0.05;
	static readonly MOUSE_SELECT_DISTANCE_THRESHOLD = 0.05;

	private pointerPos = new Vector2();
	private pointerDown = new Vector2();
	private raycaster = new Raycaster();
	private contextMenuOpened = false;
	private longPressTimeout: number | null = null;

	constructor(private renderer: ProjectRenderer) {
		this.renderer = renderer;
		this.mouseMove = this.mouseMove.bind(this);
		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.onPointerDown = this.onPointerDown.bind(this);
		this.onPointerUp = this.onPointerUp.bind(this);
	}

	init() {
		this.raycaster.layers.enable(RENDERER_CONFIG.SELECTABLE_LAYER);

		this.renderer.canvas!.addEventListener('pointermove', this.mouseMove);
		this.renderer.canvas!.addEventListener('pointerdown', this.onPointerDown);
		this.renderer.canvas!.addEventListener('pointerup', this.onPointerUp);
	}

	private mouseMove(event: PointerEvent) {
		event.preventDefault();
		this.setPointerPos(event);

		const sofaModule = this.getIntersectedModule();
		this.renderer.moduleManager.sofaModulesMap.forEach((sofaModule) => {
			sofaModule.hovered = false;
		});

		if (sofaModule) {
			document.body.style.cursor = 'pointer';
			sofaModule.hovered = true;
		} else document.body.style.cursor = 'auto';
	}

	private onPointerDown(event: PointerEvent) {
		switch (event.pointerType) {
			case 'mouse':
				this.handleMouseDown(event);
				break;
			case 'touch':
				this.handleTouchStart(event);
				break;
		}
	}

	private handleTouchStart(event: PointerEvent) {
		event.preventDefault();
		this.setPointerPos(event);
		this.pointerDown.copy(this.pointerPos);

		const sofaModule = this.getIntersectedModule();

		if (!sofaModule) {
			this.hideContextMenu();
			return;
		}

		this.longPressTimeout = setTimeout(() => {
			const dist = this.pointerDown.distanceTo(this.pointerPos);
			if (dist > InputHandler.TOUCH_DESELECT_THRESHOLD) return;

			sofaModule.selected = false;
			this.contextMenuOpened = true;

			this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
				moduleId: sofaModule.id,
				position: [event.clientX, event.clientY],
				deleteable: sofaModule.isDeleteable(),
				swapDetails: sofaModule.getOccupiedSlotsData(),
			});

			return;
		}, InputHandler.LONG_PRESS_DELAY);
	}

	private handleMouseDown(event: MouseEvent) {
		this.pointerDown.copy(this.pointerPos);

		if (event.button === MouseButton.MIDDLE) return;

		const sofaModule = this.getIntersectedModule();
		if (!sofaModule) this.hideContextMenu();
	}

	private getIntersectedModule() {
		const intersects = this.raycaster.intersectObjects(
			this.renderer.collection.children,
			true
		);

		const intersectedObject = intersects[0]?.object;
		if (!intersectedObject) return null;

		const sofaModuleGroup =
			RenderUtils.searchSofaModuleGroup(intersectedObject);
		if (!sofaModuleGroup) return null;

		const sofaModule = this.renderer.moduleManager.sofaModulesMap.get(
			sofaModuleGroup.userData[UserDataProp.SOFA_MODULE_ID]
		);
		if (!sofaModule) return null;

		return sofaModule;
	}

	private setPointerPos(event: PointerEvent) {
		const rect = this.renderer.canvas!.getBoundingClientRect();
		this.pointerPos.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		this.pointerPos.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		this.raycaster.setFromCamera(
			this.pointerPos,
			this.renderer.cameraController.camera
		);
	}

	private onPointerUp(event: PointerEvent) {
		switch (event.pointerType) {
			case 'mouse':
				this.handleMouseUp(event);
				break;
			case 'touch':
				this.handleTouchEnd(event);
				break;
		}
	}

	private handleMouseUp(event: PointerEvent) {
		event.preventDefault();

		const distance = this.pointerDown.distanceTo(this.pointerPos);
		const sofaModule = this.getIntersectedModule();

		this.pointerDown.set(0, 0);
		if (distance > InputHandler.MOUSE_SELECT_DISTANCE_THRESHOLD) return;
		if (!sofaModule) return;

		if (event.button === MouseButton.RIGHT) {
			this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
				moduleId: sofaModule.id,
				position: [event.clientX, event.clientY],
				deleteable: sofaModule.isDeleteable(),
				swapDetails: sofaModule.getOccupiedSlotsData(),
			});
			sofaModule.selected = true;
			this.contextMenuOpened = true;
			return;
		}

		this.renderer.selectModule(sofaModule);
	}

	private handleTouchEnd(event: PointerEvent) {
		event.preventDefault();

		if (this.contextMenuOpened) return;

		const distance = this.pointerDown.distanceTo(this.pointerPos);
		const sofaModule = this.getIntersectedModule();

		this.pointerDown.set(0, 0);
		if (distance > InputHandler.MOUSE_SELECT_DISTANCE_THRESHOLD) return;
		if (!sofaModule) return;

		this.renderer.selectModule(sofaModule);
	}

	private hideContextMenu() {
		this.renderer.deselectAll();
		this.contextMenuOpened = false;
		this.renderer.emitter.emit(ThreeEvent.CONTEXT_MENU, {
			moduleId: null,
			position: [0, 0],
			deleteable: false,
			swapDetails: {
				slotTypes: [],
				modulesIds: [],
			},
		});
	}

	clearLongPressTimeout() {
		if (this.longPressTimeout !== null) {
			clearTimeout(this.longPressTimeout);
			this.longPressTimeout = null;
		}
	}
}
